import React from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  MenuItem,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ArrowRight, Lock, LockOpen } from 'lucide-react';
import {
  ErrorState,
  Meter,
  SectionCard,
  StatusPill,
  TableCard,
  TableEmptyRow,
  TableSkeletonRows,
  Tag,
  useDelayedFlag,
} from '../../../components/ui';
import { sbg } from '../../../theme/sbgTokens';
import { MISSING, formatCount, formatNumber } from '../../../utils/format';
import { asSentence, getHolidayDayLabel, isUncappedHolidayShiftKey, tidyRange } from './helpers';
import type { WeekendHolidayDay, WeekendShiftAvailability, WeekendShiftRosterData } from './types';

const COLUMNS = 7;

export interface CoverageTabProps {
  rosterData: WeekendShiftRosterData | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  weekendLabel: string;
  availabilityByKey: Map<string, WeekendShiftAvailability>;
  holidayDayDraft: WeekendHolidayDay;
  onHolidayDayDraftChange: (day: WeekendHolidayDay) => void;
  holidayLabelDraft: string;
  onHolidayLabelDraftChange: (label: string) => void;
  savingHoliday: boolean;
  onSaveHoliday: () => void;
  onRequestRemoveHoliday: () => void;
  onShowOverCapReps: () => void;
  /** Open the roster narrowed to one shift. */
  onShowShiftReps: (shiftKey: string) => void;
}

/** Per-shift coverage for the selected weekend, beside the holiday-shift setting. */
const CoverageTab: React.FC<CoverageTabProps> = ({
  rosterData,
  loading,
  error,
  onRetry,
  weekendLabel,
  availabilityByKey,
  holidayDayDraft,
  onHolidayDayDraftChange,
  holidayLabelDraft,
  onHolidayLabelDraftChange,
  savingHoliday,
  onSaveHoliday,
  onRequestRemoveHoliday,
  onShowOverCapReps,
  onShowShiftReps,
}) => {
  const showSkeleton = useDelayedFlag(!rosterData && loading);
  const summary = rosterData?.summary;
  const overCapCount = summary?.overCapCount ?? 0;
  const cap = rosterData?.standardShiftCap;

  const renderRows = () => {
    if (!rosterData && loading) return <TableSkeletonRows columns={COLUMNS} rows={4} />;
    if (!rosterData && error) {
      return (
        <TableRow sx={{ '&&:hover': { bgcolor: 'transparent' } }}>
          <TableCell colSpan={COLUMNS} sx={{ borderBottom: 0 }}>
            <ErrorState compact title="Couldn’t load shift coverage" body={asSentence(error)} onRetry={onRetry} />
          </TableCell>
        </TableRow>
      );
    }
    if (!rosterData || rosterData.shiftOptions.length === 0) {
      return (
        <TableEmptyRow
          colSpan={COLUMNS}
          title="No shifts for this weekend"
          body="Saturday and Sunday shifts appear here once the weekend opens for signups."
        />
      );
    }

    return rosterData.shiftOptions.map((option) => {
      const availability = availabilityByKey.get(option.key);
      const count = availability?.count ?? rosterData.summary.shiftCounts?.[option.key] ?? 0;
      const uncapped = availability ? availability.uncapped : isUncappedHolidayShiftKey(option.key);
      // Same fallback the roster has always used when availability is missing.
      const capacity = uncapped ? null : availability?.capacity ?? 13;
      const remaining = uncapped ? null : availability?.remaining ?? Math.max((capacity ?? 0) - count, 0);
      const full = Boolean(availability?.isFull);
      const startConfirmed = rosterData.summary.confirmedShiftCounts?.[option.key] ?? 0;
      const endConfirmed = rosterData.summary.endConfirmedShiftCounts?.[option.key] ?? 0;
      const shortLabel = tidyRange(option.shortLabel);

      return (
        <TableRow key={option.key}>
          <TableCell sx={{ minWidth: 220 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="labelMd" component="span" sx={{ color: sbg.text.primary }}>
                {shortLabel}
              </Typography>
              {uncapped && <Tag sx={{ height: 20, px: 1 }}>Holiday</Tag>}
            </Box>
            <Typography variant="body2" sx={{ color: sbg.text.secondary }}>
              {tidyRange(option.fullLabel)}
            </Typography>
          </TableCell>
          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
            {capacity !== null ? `${formatNumber(count)} of ${formatNumber(capacity)}` : formatNumber(count)}
          </TableCell>
          <TableCell sx={{ minWidth: 120 }}>
            {capacity !== null ? (
              <Meter
                value={Math.min(count, capacity)}
                max={capacity}
                tone={full ? 'success' : 'brand'}
                aria-label={`${shortLabel}: ${formatNumber(count)} of ${formatNumber(capacity)} spots filled`}
              />
            ) : (
              <Typography variant="body2" component="span" sx={{ color: sbg.text.tertiary }}>{MISSING}</Typography>
            )}
          </TableCell>
          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
            {remaining === null ? (
              <Typography variant="body2" component="span" sx={{ color: sbg.text.secondary }}>No cap</Typography>
            ) : full ? (
              <StatusPill tone="success">Full</StatusPill>
            ) : (
              formatNumber(remaining)
            )}
          </TableCell>
          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
            {`${formatNumber(startConfirmed)} of ${formatNumber(count)}`}
          </TableCell>
          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
            {`${formatNumber(endConfirmed)} of ${formatNumber(count)}`}
          </TableCell>
          <TableCell align="right" sx={{ width: 56 }}>
            <Tooltip title={`Show reps on ${shortLabel}`}>
              <IconButton size="small" aria-label={`Show reps on ${shortLabel}`} onClick={() => onShowShiftReps(option.key)}>
                <ArrowRight size={16} />
              </IconButton>
            </Tooltip>
          </TableCell>
        </TableRow>
      );
    });
  };

  const holiday = rosterData?.holiday;
  const holidayDayWord = getHolidayDayLabel(holidayDayDraft);
  const enabledDayWord = getHolidayDayLabel(holiday?.holidayDay);
  const holidayLocked = Boolean(holiday?.locked);
  const holidayDisabled = !rosterData || holidayLocked || savingHoliday;

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 3,
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) 360px' },
        alignItems: 'start',
      }}
    >
      <Box sx={{ display: 'grid', gap: 2, minWidth: 0 }}>
        {overCapCount > 0 && cap !== undefined && (
          <Alert
            severity="warning"
            action={
              <Button size="small" variant="text" color="inherit" onClick={onShowOverCapReps}>
                Show reps over the cap
              </Button>
            }
          >
            {`${formatCount(overCapCount, 'rep is', 'reps are')} over the ${cap}-shift Saturday and Sunday cap for ${weekendLabel}.`}
          </Alert>
        )}
        <TableCard
          aria-label={`Shift coverage, ${weekendLabel}`}
          toolbar={
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h4" component="h2" sx={{ color: sbg.text.primary }}>
                Shift coverage
              </Typography>
              <Typography variant="body2" sx={{ color: sbg.text.secondary, mt: 0.25 }}>
                Each shift needs a start check-in in its first 30 minutes and an end check-in in its last 30 minutes.
              </Typography>
            </Box>
          }
        >
          <Table sx={{ minWidth: 760 }} aria-busy={loading || undefined}>
            <TableHead>
              <TableRow>
                <TableCell>Shift</TableCell>
                <TableCell align="right">Signed up</TableCell>
                <TableCell>Fill</TableCell>
                <TableCell align="right">Spots left</TableCell>
                <TableCell align="right">Start check-ins</TableCell>
                <TableCell align="right">End check-ins</TableCell>
                <TableCell align="right">
                  <span className="sbg-sr-only">Actions</span>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{renderRows()}</TableBody>
          </Table>
        </TableCard>
      </Box>

      <SectionCard
        title="Holiday shifts"
        description={
          !rosterData
            ? 'Open Friday or Monday shifts on a holiday weekend.'
            : holidayLocked
              ? holiday?.enabled
                ? `${enabledDayWord} AM and PM shifts are open as extras with no cap.`
                : 'No holiday shifts. Only Saturday and Sunday shifts are open.'
              : holiday?.enabled
                ? `${enabledDayWord} AM and PM shifts are open as extras with no cap. Reps still cap at ${cap} Saturday or Sunday shifts.`
                : `Only Saturday and Sunday shifts are open, with a cap of ${cap} per rep. Add a Friday or Monday holiday to open two extra shifts with no cap.`
        }
      >
        {!rosterData && error ? (
          <Typography variant="body2" sx={{ color: sbg.text.secondary }}>
            The holiday setting loads with the roster. Use Try again on shift coverage to reload it.
          </Typography>
        ) : !rosterData ? (
          <Box aria-hidden="true" sx={{ display: 'grid', gap: 1.5, visibility: showSkeleton ? 'visible' : 'hidden' }}>
            <Skeleton variant="rounded" height={22} width="50%" />
            <Skeleton variant="rounded" height={36} />
            <Skeleton variant="rounded" height={36} />
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {holiday?.enabled ? (
                <StatusPill tone="info">
                  {holiday.holiday?.holidayLabel
                    ? `${holiday.holiday.holidayLabel} · ${enabledDayWord} shifts open`
                    : `${enabledDayWord} shifts open`}
                </StatusPill>
              ) : (
                <StatusPill tone="neutral">No holiday</StatusPill>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, color: sbg.text.secondary }}>
              <Box component="span" sx={{ display: 'inline-flex', mt: '2px', flex: 'none' }} aria-hidden="true">
                {holidayLocked ? <Lock size={14} /> : <LockOpen size={14} />}
              </Box>
              <Typography variant="body2" sx={{ color: 'inherit' }}>
                {holidayLocked
                  ? `Locked. Changes closed ${holiday?.lockLabel}.`
                  : `Editable until ${holiday?.lockLabel}.`}
              </Typography>
            </Box>
            {!holidayLocked && (
              <>
                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '140px minmax(0, 1fr)', lg: '1fr' } }}>
                  <TextField
                    select
                    label="Day"
                    value={holidayDayDraft}
                    onChange={(event) => onHolidayDayDraftChange(event.target.value as WeekendHolidayDay)}
                    disabled={holidayDisabled}
                    fullWidth
                  >
                    <MenuItem value="friday">Friday</MenuItem>
                    <MenuItem value="monday">Monday</MenuItem>
                  </TextField>
                  <TextField
                    label="Holiday name"
                    placeholder="e.g. Juneteenth"
                    value={holidayLabelDraft}
                    onChange={(event) => onHolidayLabelDraftChange(event.target.value)}
                    disabled={holidayDisabled}
                    helperText={!holidayLabelDraft.trim() ? 'Name the holiday to open its shifts.' : undefined}
                    fullWidth
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {holiday?.enabled && (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={onRequestRemoveHoliday}
                      disabled={holidayDisabled}
                    >
                      Remove holiday
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    onClick={onSaveHoliday}
                    disabled={holidayDisabled || !holidayLabelDraft.trim()}
                    aria-busy={savingHoliday || undefined}
                  >
                    {savingHoliday
                      ? 'Saving…'
                      : holiday?.enabled
                        ? `Update ${holidayDayWord} holiday`
                        : `Add ${holidayDayWord} shifts`}
                  </Button>
                </Box>
              </>
            )}
          </Box>
        )}
      </SectionCard>
    </Box>
  );
};

export default CoverageTab;
